import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TransactionsService } from '../stripe/transactions.service';
import { stripeColumns } from '../models/payment-column-config';
import { DateConverterService } from '../../../helpers/date-converter.service';
import {
  IStripeLogResponse,
  IStripeLogs,
} from '../stripe/transaction.interface';

@Component({
  selector: 'app-stripe-logs',
  templateUrl: './stripe-logs.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './stripe-logs.component.scss',
})
export class StripeLogsComponent implements OnInit {
  totalResults: number = 0;
  pageSize: number = 20;
  pageOffset: number = 0; // Default page offset
  pageIndex: number = 0;

  stripeForm!: FormGroup;

  stripeId: number = 0;
  stripeData: IStripeLogs[] = []; // Store the account data
  isAccountDetailsEditable: boolean = false;
  private dataSubscription: Subscription = new Subscription(); // To unsubscribe from BehaviorSubject
  private routeParamSubscription: Subscription = new Subscription(); // To unsubscribe from route params

  columns = stripeColumns;
  pageLimit = 1000;
  currentPageSize: number = 20;
  currentPageIndex: number = 1; // Default page index
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  totalRecords: number = 0;
  dataSource = new MatTableDataSource<IStripeLogs>([]);
  status: string | undefined;
  showLogForm: boolean = false;
  public displayedColumns: string[] = this.columns.map((c) => c.key);
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dateConvertorService: DateConverterService,
    private fb: FormBuilder,
    private _snackBar: MatSnackBar,
    private transactionService: TransactionsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.routeParamSubscription = this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.stripeId = Number(id);
        this.getLogsData();
      }
    });
  }
  getLogsData() {
    this.transactionService
      .getTransactionLogsById(this.stripeId.toString())
      .subscribe((data: IStripeLogResponse) => {
        if (data && data.value) {
          this.stripeData = data.value;
          this.cdr.detectChanges();
          // ✅ FIRST: transform dates BEFORE binding to table
          const transformed = this.stripeData.map((value: IStripeLogs) => ({
            ...value,
            creationDate: this.dateConvertorService
              .getTimezoneAgnosticDateIsoString(value.creationDate, false)
              .split('T')[0],
          }));

          // ✅ SECOND: assign transformed data to MatTableDataSource
          this.dataSource.data = transformed;

          // Ensure paginator & sort attach AFTER DOM loads
          setTimeout(() => {
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
          });

          this.totalResults = transformed.length;
        } else {
          this._snackBar.open('No id is found', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
      });
  }

  sortData(sort: Sort): void {
    // Assuming your data is fetched and available in transactions.data
    const data = this.stripeData.slice();

    if (!sort.active || sort.direction === '') {
      // No sort active, return to original order or do nothing
      this.stripeData = data;
      return;
    }

    this.stripeData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'id':
          return this.compare(a.id, b.id, isAsc);
        case 'creationDate':
          return this.compare(a.creationDate, b.creationDate, isAsc);
        // Add more cases for each sortable column
        default:
          return 0;
      }
    });
  }

  compare(a: number | string, b: number | string, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  goToAccountingPage() {
    this.router.navigate(['/klusup/accounting/payments']);
  }

  onPageChange(page: { pageSize: number; pageIndex: number }): void {
    this.stripeData = [];
    this.pageSize = page.pageSize; // Update page size if needed
    this.pageOffset = page.pageIndex * this.pageSize; // Calculate page offset for API calls
    this.getLogsData();
  }
}
